/**
 * LiveAgent.jsx — Real-time agent workspace.
 *
 * Tabs:
 *   1. Escalation Queue   — live list of open/pending tickets with SLA timer
 *   2. Canned Responses   — search + manage quick-reply macros
 */
import { CheckCircle, Circle, Clock, MessageSquare, Plus, Search, Trash2, Zap } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  Alert, Badge, Button, Card, Col, Form,
  Modal, Row, Spinner, Table
} from "react-bootstrap";
import APICall from "../../APICalls/APICall";
import TabComponent from "../../components/TabComponent";
import { useConfirm } from "../../components/useConfirm";
import { useToast } from "../../components/useToast";
import useConsoleEvents from "../../hooks/useConsoleEvents";
import { useAuth } from "../../Context/AuthContext";
import ChatWidget from "../ChatWidget/ChatWidget";
import { formatDateTime, parseUTCDate } from "../../utils/datetime";

// ─── Helpers ────────────────────────────────────────────────────────────────

const PRIORITY_VARIANT = { urgent: "danger", high: "warning", medium: "primary", low: "secondary" };

function minutesSince(isoStr) {
  // parseUTCDate, not new Date(): backend timestamps carry no zone suffix, so a
  // naive parse reads UTC as local and inflates elapsed by the whole offset.
  const d = parseUTCDate(isoStr);
  if (!d) return null;
  return Math.floor((Date.now() - d.getTime()) / 60000);
}

function SLABadge({ createdAt, priority }) {
  const thresholds = { urgent: 15, high: 60, medium: 120, low: 480 };
  const elapsed = minutesSince(createdAt);
  if (elapsed === null) return null;
  const limit = thresholds[priority] || 120;
  const pct = Math.min((elapsed / limit) * 100, 100);
  const variant = pct >= 100 ? "danger" : pct >= 75 ? "warning" : "success";
  return (
    <Badge bg={variant} className="ms-1">
      <Clock size={10} className="me-1" />
      {elapsed}m
    </Badge>
  );
}

// ─── Tab 1: Escalation Queue ─────────────────────────────────────────────────

function EscalationQueueTab() {
  const [tickets, setTickets]     = useState([]);
  const [agents, setAgents]       = useState([]);
  const [onlineIds, setOnlineIds] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [assignModal, setAssignModal] = useState(null);
  const [assignTo, setAssignTo]   = useState("");
  const [saving, setSaving]       = useState(false);
  const [statusFilter, setStatusFilter] = useState("active");
  const [chatTicket, setChatTicket] = useState(null);   // ticket whose chat is open
  const { showToast, ToastContainer } = useToast();
  const { confirm, ConfirmDialog } = useConfirm();

  // An "agent" role sees only themselves; everyone else (employee) sees the whole
  // roster. The ticket list is already agent-scoped by the backend.
  const { user, profileType } = useAuth();
  const isSupervisor = profileType !== "agent";
  const myId = user?.id;
  const rosterAgents = isSupervisor ? agents : agents.filter(a => a.id === myId);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      // Agent list comes from /conversation/assignment/config (Live-Agent scoped,
      // org derived from the token) — NOT /hrms/users, which needs User-Management
      // permission and 403s for agent supervisors, leaving the assign dropdown empty.
      const [escRes, cfgRes, onlineRes] = await Promise.all([
        APICall.getT("/conversation/escalation"),
        APICall.getT("/conversation/assignment/config"),
        APICall.getT("/conversation/agents/online"),
      ]);
      const list = Array.isArray(escRes) ? escRes : (escRes?.escalations || []);
      setTickets(list);
      setAgents(Array.isArray(cfgRes?.agents) ? cfgRes.agents : []);
      setOnlineIds(onlineRes?.online_agent_ids || []);
    } catch (e) {
      setError("Failed to load escalations.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Real-time updates: refresh silently only when the server pushes a relevant
  // event (no timer polling → no periodic spinner / table flash).
  useConsoleEvents(useCallback((evt) => {
    if (evt?.type === "ESCALATION_EVENT" || evt?.type === "AGENT_PRESENCE") {
      load(true);
    }
  }, [load]));

  const filtered = tickets.filter(t => {
    if (!statusFilter) return true;
    const status = (t.status || "").toLowerCase();
    if (statusFilter === "active") {
      return ["open", "pending", "assigned", "reopened"].includes(status);
    }
    return status === statusFilter.toLowerCase();
  });

  const handleAssign = async () => {
    if (!assignModal || !assignTo) return;
    setSaving(true);
    try {
      await APICall.postT(`/conversation/update_escalation/${assignModal.id}`, {
        assigned_to: assignTo,
        priority: assignModal.priority,
        reason: assignModal.reason,
      });
      setAssignModal(null);
      setAssignTo("");
      load();
    } catch (e) {
      showToast(e?.message || "Failed to assign ticket.", "danger");
    } finally {
      setSaving(false);
    }
  };

  const handleResolve = async (id) => {
    if (!await confirm("Mark this ticket as resolved?", { confirmLabel: "Resolve", variant: "success" })) return;
    try {
      await APICall.postT(`/conversation/update_escalation/${id}`, { status: "RESOLVED" });
      load();
      showToast("Ticket resolved.", "success");
    } catch (e) {
      showToast(e?.message || "Failed to resolve ticket.", "danger");
    }
  };

  const handleClose = async (id, { fromModal = false } = {}) => {
    if (!await confirm("Close this ticket? The conversation will end.", { confirmLabel: "Close ticket", variant: "danger" })) return;
    try {
      await APICall.postT(`/conversation/update_escalation/${id}`, { status: "CLOSED" });
      if (fromModal) setChatTicket(null);
      load();
      showToast("Ticket closed.", "success");
    } catch (e) {
      showToast(e?.message || "Failed to close ticket.", "danger");
    }
  };

  const isTerminal = (s) => ["resolved", "closed", "deleted", "cancelled", "canceled"].includes((s || "").toLowerCase());

  // If the ticket whose chat is open changes status (e.g. it gets closed/cancelled
  // while the agent has the modal open), sync the open ticket so the chat becomes
  // read-only (typing box hidden) instead of showing a stale editable box.
  useEffect(() => {
    if (!chatTicket) return;
    const fresh = tickets.find(t => t.id === chatTicket.id);
    if (fresh && fresh.status !== chatTicket.status) {
      setChatTicket(prev => (prev ? { ...prev, status: fresh.status } : prev));
    }
  }, [tickets, chatTicket]);

  if (loading) return <div className="text-center py-5"><Spinner /></div>;

  return (
    <div className="p-3">
      <ToastContainer /><ConfirmDialog />
      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="border-0 shadow-sm mb-3 p-3">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <strong className="d-flex align-items-center gap-1" style={{ fontSize: 13 }}>
            <Circle size={8} fill="#22c55e" stroke="none" /> {isSupervisor ? "Live Agents" : "My Status"}
          </strong>
          {rosterAgents.filter(a => onlineIds.includes(a.id)).length === 0 ? (
            <span className="text-muted" style={{ fontSize: 13 }}>
              {isSupervisor ? "No agents online" : "You are offline"}
            </span>
          ) : (
            rosterAgents.filter(a => onlineIds.includes(a.id)).map(a => (
              <Badge key={a.id} bg="success" className="d-flex align-items-center gap-1" style={{ fontWeight: 500 }}>
                <Circle size={5} fill="#fff" stroke="none" />
                {a.fullname || a.email}
              </Badge>
            ))
          )}
          {isSupervisor && (
            <span className="ms-auto text-muted" style={{ fontSize: 12 }}>
              {rosterAgents.filter(a => onlineIds.includes(a.id)).length} / {agents.length} online
            </span>
          )}
        </div>
      </Card>

      <Row className="g-2 mb-3 align-items-center">
        <Col md={4}>
          <Form.Select size="sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="active">Active Tickets</option>
            <option value="">All Statuses</option>
            <option value="open">Open</option>
            <option value="pending">Pending</option>
            <option value="assigned">Assigned</option>
            <option value="resolved">Resolved</option>
          </Form.Select>
        </Col>
        <Col className="text-end">
          <Button size="sm" variant="outline-primary" onClick={load}>Refresh</Button>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm">
        <Table hover responsive className="mb-0 align-middle">
          <thead className="table-light">
            <tr>
              <th>Ticket</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Reason</th>
              <th>Assigned</th>
              <th>SLA</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center text-muted py-4">No tickets found.</td></tr>
            )}
            {filtered.map(t => (
              <tr key={t.id}>
                <td><strong>#{t.ticket_id || t.id}</strong></td>
                <td>
                  <Badge bg={PRIORITY_VARIANT[t.priority?.toLowerCase()] || "secondary"}>
                    {t.priority || "medium"}
                  </Badge>
                </td>
                <td>{t.status}</td>
                <td className="text-truncate" style={{ maxWidth: 160 }}>{t.reason}</td>
                <td>
                  {t.assigned_to_name ? (
                    <span className="d-flex align-items-center gap-1">
                      <Circle size={6} fill={onlineIds.includes(t.assigned_to) ? "#22c55e" : "#9ca3af"} stroke="none" />
                      {t.assigned_to_name}
                    </span>
                  ) : <span className="text-muted">Unassigned</span>}
                </td>
                <td>{isTerminal(t.status)
                  ? <span className="text-muted">—</span>
                  : <SLABadge createdAt={t.created_at} priority={t.priority?.toLowerCase()} />}</td>
                <td className="text-nowrap" style={{ fontSize: 12.5 }}>
                  <div>{formatDateTime(t.created_at)}</div>
                  {isTerminal(t.status) && t.resolved_at && (
                    <div className="text-muted" style={{ fontSize: 11.5 }}>
                      Closed {formatDateTime(t.resolved_at)}
                    </div>
                  )}
                </td>
                <td>
                  <div className="d-flex gap-2 flex-wrap">
                    {/* A finished ticket has no live chat to open. */}
                    {t.session_id && !isTerminal(t.status) && (
                      <Button size="sm" variant="primary" className="d-flex align-items-center gap-1"
                        onClick={() => setChatTicket(t)}>
                        <MessageSquare size={13} /> Chat
                      </Button>
                    )}
                    {isSupervisor && !isTerminal(t.status) && (
                      <Button size="sm" variant="outline-primary"
                        onClick={() => { setAssignModal(t); setAssignTo(t.assigned_to || ""); }}>
                        Assign
                      </Button>
                    )}
                    {!isTerminal(t.status) && (
                      <Button size="sm" variant="danger" className="d-flex align-items-center gap-1"
                        onClick={() => handleClose(t.id)}>
                        <CheckCircle size={13} /> Close
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      {/* Assign Modal */}
      <Modal show={!!assignModal} onHide={() => setAssignModal(null)} centered fullscreen="sm-down">
        <Modal.Header closeButton>
          <Modal.Title>Assign Ticket #{assignModal?.ticket_id || assignModal?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Assign to Agent</Form.Label>
            <Form.Select value={assignTo} onChange={e => setAssignTo(e.target.value)}>
              <option value="">Select agent…</option>
              {rosterAgents.map(a => (
                <option key={a.id} value={a.id}>{a.name || a.fullname || a.email}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setAssignModal(null)}>Cancel</Button>
          <Button className="primaryBtn" onClick={handleAssign} disabled={saving || !assignTo}>
            {saving ? <Spinner size="sm" /> : "Assign"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Chat Modal — read the conversation and reply to the customer inline */}
      <Modal show={!!chatTicket} onHide={() => setChatTicket(null)} size="lg" centered
        fullscreen="md-down" dialogClassName="la-chat-modal">
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2" style={{ fontSize: 16 }}>
            Ticket #{chatTicket?.ticket_id || chatTicket?.id}
            <Badge bg={PRIORITY_VARIANT[chatTicket?.priority?.toLowerCase()] || "secondary"}>
              {chatTicket?.priority || "medium"}
            </Badge>
            <span className="text-muted" style={{ fontSize: 13, fontWeight: 400 }}>{chatTicket?.status}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {chatTicket?.session_id && (
            <ChatWidget
              key={chatTicket.session_id}
              agentId={user.id}
              sessionId={chatTicket.session_id}
              title={`Ticket #${chatTicket.ticket_id || chatTicket.id}`}
              readOnly={isTerminal(chatTicket.status)}
              // No onClose: this Modal's header already has a close button.
            />
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <span className="text-muted" style={{ fontSize: 12 }}>
            {chatTicket?.assigned_to_name ? `Assigned to ${chatTicket.assigned_to_name}` : "Unassigned"}
          </span>
          <div className="d-flex gap-2">
            <Button variant="secondary" onClick={() => setChatTicket(null)}>Close window</Button>
            {chatTicket && !isTerminal(chatTicket.status) && (
              <Button variant="danger" className="d-flex align-items-center gap-1"
                onClick={() => handleClose(chatTicket.id, { fromModal: true })}>
                <CheckCircle size={15} /> Close ticket
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

// ─── Tab 2: Agent Status ─────────────────────────────────────────────────────

function AgentStatusTab() {
  const [agents, setAgents]       = useState([]);
  const [onlineIds, setOnlineIds] = useState([]);
  const [config, setConfig]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");

  // Agents see only their own status; employees see the whole roster.
  const { user, profileType } = useAuth();
  const isSupervisor = profileType !== "agent";
  const myId = user?.id;

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const [agentsRes, onlineRes, cfgRes] = await Promise.all([
        APICall.getT("/hrms/users"),
        APICall.getT("/conversation/agents/online"),
        APICall.getT("/conversation/assignment/config"),
      ]);
      // /hrms/users needs User-Management permission; for agent-supervisors it
      // 403s (returns a {detail} object). Guard so the roster degrades to the
      // assignment-config agents instead of crashing on a non-array.
      setAgents(Array.isArray(agentsRes)
        ? agentsRes
        : (Array.isArray(cfgRes?.agents) ? cfgRes.agents : []));
      setOnlineIds(onlineRes?.online_agent_ids || []);
      setConfig(cfgRes || null);
    } catch {
      setError("Failed to load agent status.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live presence/load updates via push — refresh silently, no timer polling.
  useConsoleEvents(useCallback((evt) => {
    if (evt?.type === "AGENT_PRESENCE" || evt?.type === "ESCALATION_EVENT") {
      load(true);
    }
  }, [load]));

  // Per-agent load/capacity from the auto-assignment config (keyed by id).
  const loadById = {};
  (config?.agents || []).forEach(a => { loadById[a.id] = a; });
  const cap = config?.max_concurrent_chats_per_agent;

  // Agents see only their own row; supervisors see everyone.
  const visibleAgents = isSupervisor ? agents : agents.filter(a => a.id === myId);
  const onlineCount = visibleAgents.filter(a => onlineIds.includes(a.id)).length;

  if (loading) return <div className="text-center py-5"><Spinner /></div>;

  return (
    <div className="p-3">
      {error && <Alert variant="danger">{error}</Alert>}

      {isSupervisor && (
      <Row className="g-2 mb-3">
        <Col xs={6} md={3}>
          <Card className="border-0 shadow-sm p-3 text-center">
            <div className="text-muted" style={{ fontSize: 12 }}>Online</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#16a34a" }}>{onlineCount}</div>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="border-0 shadow-sm p-3 text-center">
            <div className="text-muted" style={{ fontSize: 12 }}>Total Agents</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{agents.length}</div>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="border-0 shadow-sm p-3 text-center">
            <div className="text-muted" style={{ fontSize: 12 }}>Queued Tickets</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#d97706" }}>{config?.queued_tickets ?? 0}</div>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="border-0 shadow-sm p-3 text-center">
            <div className="text-muted" style={{ fontSize: 12 }}>Cap / Agent</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{cap ?? "—"}</div>
          </Card>
        </Col>
      </Row>
      )}

      <div className="d-flex justify-content-end mb-2">
        <Button size="sm" variant="outline-primary" onClick={load}>Refresh</Button>
      </div>

      <Card className="border-0 shadow-sm">
        <Table hover responsive className="mb-0 align-middle">
          <thead className="table-light">
            <tr>
              <th>Agent</th>
              <th>Live Status</th>
              <th>Availability</th>
              <th>Active Tickets</th>
              <th>Free Slots</th>
              <th>Auto-Assignable</th>
            </tr>
          </thead>
          <tbody>
            {visibleAgents.length === 0 && (
              <tr><td colSpan={6} className="text-center text-muted py-4">No agents found.</td></tr>
            )}
            {visibleAgents.map(a => {
              const online   = onlineIds.includes(a.id);
              const cfg      = loadById[a.id];
              const avail    = (a.availability_status || "").toUpperCase();
              const isAvail  = avail === "AVAILABLE";
              const active   = cfg?.active_tickets ?? 0;
              const slots    = cfg?.available_slots;
              // Auto-assignable = live (online) AND available AND has a free slot.
              const assignable = online && isAvail && (slots === undefined || slots > 0);
              return (
                <tr key={a.id}>
                  <td>
                    <strong>{a.fullname || a.name || a.email}</strong>
                    {a.email && a.fullname && (
                      <div className="text-muted" style={{ fontSize: 11 }}>{a.email}</div>
                    )}
                  </td>
                  <td>
                    <span className="d-flex align-items-center gap-1">
                      <Circle size={8} fill={online ? "#22c55e" : "#9ca3af"} stroke="none" />
                      <span style={{ color: online ? "#16a34a" : "#6b7280", fontWeight: 600, fontSize: 13 }}>
                        {online ? "Online" : "Offline"}
                      </span>
                    </span>
                  </td>
                  <td>
                    <Badge bg={isAvail ? "success" : "secondary"}>
                      {avail || "UNKNOWN"}
                    </Badge>
                  </td>
                  <td>{active}{cap ? ` / ${cap}` : ""}</td>
                  <td>{slots === undefined ? "—" : slots}</td>
                  <td>
                    {assignable
                      ? <Badge bg="success">Yes</Badge>
                      : <Badge bg="light" text="dark">No</Badge>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card>
      <div className="text-muted mt-2" style={{ fontSize: 12 }}>
        Only <strong>online + available</strong> agents with a free slot receive auto-assigned tickets.
        Offline agents are skipped; their tickets wait in the queue.
      </div>
    </div>
  );
}

// ─── Tab 3: Canned Responses ─────────────────────────────────────────────────

function CannedResponsesTab() {
  const [responses, setResponses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch]         = useState("");
  const [catFilter, setCatFilter]   = useState("");
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [showModal, setShowModal]   = useState(false);
  const [editing, setEditing]       = useState(null);
  const [form, setForm]             = useState({ name: "", shortcut: "", category: "", body: "" });
  const [saving, setSaving]         = useState(false);
  const [copied, setCopied]         = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (search)    params.append("search",   search);
      if (catFilter) params.append("category", catFilter);
      const res = await APICall.getT(`/canned?${params}`);
      setResponses(res?.responses || []);
      setCategories(res?.categories || []);
    } catch {
      setError("Failed to load canned responses.");
    } finally {
      setLoading(false);
    }
  }, [search, catFilter]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", shortcut: "", category: "", body: "" });
    setShowModal(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({ name: r.name, shortcut: r.shortcut || "", category: r.category || "", body: r.body });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.body) { alert("Name and body are required."); return; }
    setSaving(true);
    try {
      if (editing) {
        await APICall.postT(`/canned/${editing.id}`, form);
      } else {
        await APICall.postT("/canned", form);
      }
      setShowModal(false);
      load();
    } catch {
      alert("Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this canned response?")) return;
    try {
      await APICall.postT(`/canned/${id}/delete`, {});
      load();
    } catch {
      alert("Failed to delete.");
    }
  };

  const copyBody = (r) => {
    navigator.clipboard.writeText(r.body).catch(() => {});
    setCopied(r.id);
    setTimeout(() => setCopied(null), 1500);
    APICall.postT(`/canned/${r.id}/use`, {}).catch(() => {});
  };

  if (loading) return <div className="text-center py-5"><Spinner /></div>;

  return (
    <div className="p-3">
      {error && <Alert variant="danger">{error}</Alert>}

      <Row className="g-2 mb-3 align-items-center">
        <Col md={4}>
          <div className="input-group input-group-sm">
            <span className="input-group-text"><Search size={14} /></span>
            <Form.Control placeholder="Search responses…" value={search}
              onChange={e => setSearch(e.target.value)} />
          </div>
        </Col>
        <Col md={3}>
          <Form.Select size="sm" value={catFilter} onChange={e => setCatFilter(e.target.value)}>
            <option value="">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </Form.Select>
        </Col>
        <Col className="text-end">
          <Button size="sm" className="primaryBtn" onClick={openCreate}>
            <Plus size={14} className="me-1" /> New Response
          </Button>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm">
        <Table hover responsive className="mb-0 align-middle">
          <thead className="table-light">
            <tr>
              <th>Name</th>
              <th>Shortcut</th>
              <th>Category</th>
              <th>Body</th>
              <th>Uses</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {responses.length === 0 && (
              <tr><td colSpan={6} className="text-center text-muted py-4">No canned responses yet.</td></tr>
            )}
            {responses.map(r => (
              <tr key={r.id}>
                <td><strong>{r.name}</strong></td>
                <td>{r.shortcut ? <Badge bg="light" text="dark">/{r.shortcut}</Badge> : "—"}</td>
                <td>{r.category || "—"}</td>
                <td className="text-truncate" style={{ maxWidth: 220 }}>{r.body}</td>
                <td>{r.use_count || 0}</td>
                <td>
                  <div className="d-flex gap-2">
                    <Button size="sm" variant="outline-secondary" onClick={() => copyBody(r)}>
                      {copied === r.id ? <CheckCircle size={13} /> : <MessageSquare size={13} />}
                    </Button>
                    <Button size="sm" variant="outline-primary" onClick={() => openEdit(r)}>
                      Edit
                    </Button>
                    <Button size="sm" variant="outline-danger" onClick={() => handleDelete(r.id)}>
                      <Trash2 size={13} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      {/* Create / Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered fullscreen="sm-down">
        <Modal.Header closeButton>
          <Modal.Title>{editing ? "Edit Response" : "New Canned Response"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="g-2">
              <Col md={8}>
                <Form.Group>
                  <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control size="sm" value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Shortcut</Form.Label>
                  <Form.Control size="sm" placeholder="/greeting" value={form.shortcut}
                    onChange={e => setForm({ ...form, shortcut: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Category</Form.Label>
                  <Form.Control size="sm" placeholder="e.g. Billing, Support" value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Body <span className="text-danger">*</span></Form.Label>
                  <Form.Control as="textarea" rows={4} size="sm" value={form.body}
                    onChange={e => setForm({ ...form, body: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button className="primaryBtn" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

const LiveAgent = () => {
  const pageContent = {
    title: "Live Agent",
    subTitle: "Real-time escalation queue and agent tools",
    tabs: [
      { tabTitle: "Escalation Queue", tabKey: "queue",  tabContent: <EscalationQueueTab /> },
      { tabTitle: "Agent Status",     tabKey: "agents", tabContent: <AgentStatusTab /> },
      { tabTitle: "Canned Responses", tabKey: "canned", tabContent: <CannedResponsesTab /> },
    ],
  };

  return (
    <div className="h-100">
      <TabComponent pageContent={pageContent} />
    </div>
  );
};

export default LiveAgent;
